import {
  fetchProjectAsset,
  getProjectAssetUrl,
} from "components/SmartSurveyor/SmartSurveyorV3/utils/signedAssetUrls";
import type {
  ResearchReportData,
  BoqExtractionResult,
  Manifest,
} from "../ResearchReportPage/types";

function agentOutputKey(projectId: string, filename: string): string {
  return `smart-surveyor/projects/${projectId}/agent_output/${filename}`;
}

async function fetchJson<T>(projectId: string, key: string): Promise<T | null> {
  try {
    const res = await fetchProjectAsset({ projectId, key });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export const fetchManifest = async (
  projectId: string,
): Promise<Manifest | null> => {
  return fetchJson<Manifest>(
    projectId,
    agentOutputKey(projectId, "manifest.json"),
  );
};

/**
 * Fetch research report, preferring the localized version when the user's
 * language matches one of the project's available_languages.
 *
 * For example, if lang="de" and research_report_de.json exists on S3, it
 * will be fetched instead of research_report.json.
 */
export const fetchResearchReport = async (
  projectId: string,
  lang?: string,
  availableLanguages?: string[],
): Promise<ResearchReportData | null> => {
  // Try localized version first (only for non-English)
  if (lang && lang !== "en" && availableLanguages?.includes(lang)) {
    const localized = await fetchJson<ResearchReportData>(
      projectId,
      agentOutputKey(projectId, `research_report_${lang}.json`),
    );
    if (localized) return localized;
  }
  return fetchJson<ResearchReportData>(
    projectId,
    agentOutputKey(projectId, "research_report.json"),
  );
};

export const fetchAgentBoqExtraction = async (
  projectId: string,
): Promise<BoqExtractionResult | null> => {
  return fetchJson<BoqExtractionResult>(
    projectId,
    agentOutputKey(projectId, "boq_extraction.json"),
  );
};

export async function pdfFileUrl(
  projectId: string,
  filename: string,
): Promise<string> {
  const pdfUrl = await getProjectAssetUrl({
    projectId,
    key: `smart-surveyor/projects/${projectId}/files/${filename}`,
  });
  return pdfUrl ?? "";
}

export const stripPdfExtension = (filename: string): string =>
  filename.replace(/\.pdf\s?/gi, "");
